import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ethers } from 'ethers';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import Pill from '../components/Pill';
import { fmt, weiToEth, short } from '../lib/format';
import { useAccount } from '../hooks/useWallet';
import { useReadContracts } from '../hooks/useContracts';
import { useIsActiveAuditor } from '../hooks/useAuditorsInPool';
import { useBalanceOf } from '../hooks/useToken';
import { useWriteContract } from '../hooks/useWriteContract';
import { ADDRESSES } from '../lib/chain';
import {
  useFirmCount,
  useFirmIdOf,
  useMinStakeJunior,
  useMinStakeSenior,
  useMinStakePrincipal,
  useUnstakeCooldown,
  AUDIT_CATEGORIES,
  SENIORITY_LEVELS,
  FIRM_STATUSES,
  NOT_FOUND,
  type FirmRow,
  type FirmStats,
} from '../hooks/useAuditorRegistry';

export default function Auditors() {
  const account = useAccount();
  const contracts = useReadContracts();

  const { data: countBn } = useFirmCount();
  const isActive = useIsActiveAuditor(account);
  const myFirmId = useFirmIdOf(account);

  const count = countBn !== undefined ? Number(countBn) : 0;

  /** Fan-out: firm row + stats per firmId. */
  const firmQueries = useQueries({
    queries: Array.from({ length: count }, (_, i) => ({
      queryKey: ['metha', 'registry', 'getFirm', i] as const,
      queryFn: async () => (await contracts.registry.getFirm(BigInt(i))) as unknown as FirmRow,
      staleTime: 30_000,
    })),
  });

  const statsQueries = useQueries({
    queries: Array.from({ length: count }, (_, i) => ({
      queryKey: ['metha', 'registry', 'getFirmStats', i] as const,
      queryFn: async () => (await contracts.registry.getFirmStats(BigInt(i))) as unknown as FirmStats,
      staleTime: 30_000,
    })),
  });

  /** Resolve held categories per firm by querying hasCategory(firmId, c) for c in 0..6 */
  const categoriesQueries = useQueries({
    queries: Array.from({ length: count * AUDIT_CATEGORIES.length }, (_, idx) => {
      const firmId = Math.floor(idx / AUDIT_CATEGORIES.length);
      const cat = idx % AUDIT_CATEGORIES.length;
      return {
        queryKey: ['metha', 'registry', 'hasCategory', firmId, cat] as const,
        queryFn: async () => (await contracts.registry.hasCategory(BigInt(firmId), cat)) as boolean,
        staleTime: 60_000,
      };
    }),
  });

  const firms = useMemo(() => {
    return firmQueries
      .map((q, i) => {
        if (!q.data) return null;
        const stats = statsQueries[i]?.data;
        const cats = AUDIT_CATEGORIES
          .map((name, ci) => ({ name, held: !!categoriesQueries[i * AUDIT_CATEGORIES.length + ci]?.data }))
          .filter((c) => c.held)
          .map((c) => c.name);
        return { id: i, row: q.data, stats, categories: cats };
      })
      // Note: `stats: FirmStats | undefined` (required key, undefined-allowed),
      // NOT `stats?: FirmStats` (optional key). Subtle TS distinction that matters
      // here because the .map above always sets the key. AUDIT_CATEGORIES is a
      // readonly tuple so `categories` narrows to its union of literals.
      .filter((x): x is {
        id: number;
        row: FirmRow;
        stats: FirmStats | undefined;
        categories: (typeof AUDIT_CATEGORIES)[number][];
      } => x !== null);
  }, [firmQueries, statsQueries, categoriesQueries]);

  const active   = firms.filter(({ row }) => Number(row.status) === 1);
  const frozen   = firms.filter(({ row }) => Number(row.status) === 2);
  const banned   = firms.filter(({ row }) => Number(row.status) === 3);
  const inactive = firms.filter(({ row }) => Number(row.status) === 0);

  const avgRep = active.length > 0
    ? active.reduce((s, { row }) => s + Number(row.reputation), 0) / active.length
    : 0;
  const totalMilestones = firms.reduce(
    (s, { stats }) => s + Number(stats?.assignmentsApproved ?? 0n), 0
  );
  const totalStakeMeth = firms.reduce((s, { row }) => s + weiToEth(row.stake), 0);

  const myFirmKnown = myFirmId.data !== undefined && myFirmId.data !== NOT_FOUND;
  const myFirmRow = myFirmKnown && myFirmId.data !== undefined
    ? firms.find((f) => BigInt(f.id) === myFirmId.data)
    : undefined;
  // Held category indices for the user's firm — derived from the per-firm
  // hasCategory fan-out we already do above. We index by enum position so the
  // FirmManagementCard can toggle without re-querying.
  const myFirmHeldCategoryIdx = useMemo(() => {
    if (!myFirmRow) return new Set<number>();
    const base = myFirmRow.id * AUDIT_CATEGORIES.length;
    const held = new Set<number>();
    AUDIT_CATEGORIES.forEach((_, ci) => {
      if (categoriesQueries[base + ci]?.data === true) held.add(ci);
    });
    return held;
  }, [myFirmRow, categoriesQueries]);

  return (
    <Page ctxLabel="auditors" ctx="auditor" title="Metha · Auditing firms" docsPath="guides/auditor" docsLabel="Auditor guide">
      <Crumbs crumbs={[{ to: '/', label: 'Overview' }, { label: 'Auditors' }]} />

      <div className="ph">
        <div>
          <h1>Auditing firms <span className="acc">/ {count} registered</span></h1>
          <p className="lede">
            v3 · AuditorRegistryV2. Firms register with seniority tier (Junior /
            Senior / Principal), stake METH, and self-declare audit categories
            aligned with BARDA / ARPA-H scope. DSMB veto rights are granted by
            oracle attestation for clinical work.
          </p>
        </div>
        <div className="meta">
          <span>ACTIVE <b>{active.length}</b></span>
          <span>FROZEN <b>{frozen.length}</b></span>
          <span>YOUR STATUS <b style={{ color: isActive.data ? 'var(--green)' : 'var(--muted)' }}>
            {!account ? 'connect wallet' :
              isActive.data ? `active · firm #${String(myFirmId.data ?? '?')}` :
              myFirmKnown ? `registered · ${FIRM_STATUSES[Number(myFirmRow?.row.status ?? 0)]}` :
              'not registered'}
          </b></span>
        </div>
      </div>

      <div className="kpi-row mb-24" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <Kpi l="AVG REPUTATION" v={active.length > 0 ? avgRep.toFixed(0) : '—'} d={`${active.length} active firms`} />
        <Kpi l="MILESTONES APPROVED" v={String(totalMilestones)} d="lifetime · all firms" tone="up" />
        <Kpi l="TOTAL STAKE" v={<>{fmt.meth(totalStakeMeth)}<span className="u">METH</span></>} d="locked across firms" />
        <Kpi l="REGISTERED" v={String(count)} d={`${active.length} active, ${frozen.length} frozen, ${banned.length} banned, ${inactive.length} inactive`} />
        <Kpi l="LIVE FETCH" v={firmQueries.filter((q) => q.isLoading).length === 0 ? '✓' : '…'} d={`${firms.length} / ${count} loaded`} />
      </div>

      <div className="row r-3u1 mb-24">
        <div>
          <div className="row r-3 mb-14">
            {active.map(({ id, row, stats, categories }) => (
              <FirmCard key={id} id={id} row={row} stats={stats} categories={categories} />
            ))}
            {active.length === 0 && !firmQueries.some((q) => q.isLoading) && (
              <div className="panel" style={{ gridColumn: '1 / -1', padding: 24, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
                No active firms yet. Register below to become the first.
              </div>
            )}
          </div>

          {(frozen.length > 0 || banned.length > 0 || inactive.length > 0) && (
            <div className="panel">
              <div className="p-head"><h3>Non-active firms</h3>
                <span className="src"><code>registry.getFirm(id)</code></span></div>
              <div className="tbl">
                <div className="th" style={{ gridTemplateColumns: '60px 1fr 110px 100px 110px' }}>
                  <span>ID</span><span>WALLET</span><span>STATUS</span><span>STAKE</span><span>STRIKES</span>
                </div>
                {[...frozen, ...banned, ...inactive].map(({ id, row, stats }) => (
                  <div key={id} className="tr" style={{ gridTemplateColumns: '60px 1fr 110px 100px 110px' }}>
                    <span className="pid">#{String(id).padStart(3, '0')}</span>
                    <span className="who">{short(row.wallet)}<small>{row.name}</small></span>
                    <span><Pill tone={Number(row.status) === 3 ? 'gray' : 'amber'}>{FIRM_STATUSES[Number(row.status)]}</Pill></span>
                    <span>{fmt.meth(weiToEth(row.stake))} METH</span>
                    <span className="mono" style={{ fontSize: 11 }}>
                      m{Number(stats?.strikesMinor ?? 0n)}·M{Number(stats?.strikesMajor ?? 0n)}·C{Number(stats?.strikesCritical ?? 0n)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside>
          <RegisterCard
            account={account}
            myFirmId={myFirmId.data}
            myFirmRow={myFirmRow?.row}
          />

          {/* Once a wallet has a registered firm, surface the post-registration
              actions: stake mgmt (increase / request-unstake / finalize), the
              category scope (add/remove), and seniority upgrade. Hidden for
              non-registered wallets so it doesn't compete with the register CTA. */}
          {myFirmKnown && myFirmRow && (
            <FirmManagementCard
              firmId={Number(myFirmId.data!)}
              row={myFirmRow.row}
              heldCategoryIdx={myFirmHeldCategoryIdx}
            />
          )}

          <div className="panel">
            <div className="p-head"><h3>Reward economics</h3><span className="src">operations stash · 20%</span></div>
            <div className="p-body">
              <div className="estim"><span>+10 REP / milestone signed-off correctly</span><b style={{ color: 'var(--green)' }}>winning vote</b></div>
              <div className="estim"><span>−5 REP / minority-loser</span><b style={{ color: 'var(--red)' }}>against threshold</b></div>
              <div className="estim"><span>METH reward</span><b><code>auditorReward</code> per approval</b></div>
              <div className="estim"><span>Slashing</span><b>Minor 3-5-10% · Major 10-25-50% · Critical 100%+ban</b></div>
            </div>
          </div>
        </aside>
      </div>
    </Page>
  );
}

/* -------------------- cards -------------------- */

function FirmCard({
  id, row, stats, categories,
}: {
  id: number;
  row: FirmRow;
  stats: FirmStats | undefined;
  categories: string[];
}) {
  const rep = Number(row.reputation);
  const repPct = Math.min(100, (rep / 300) * 100);
  const seniorityName = SENIORITY_LEVELS[Number(row.seniority)] ?? '?';
  return (
    <div className="prof">
      <div className="top">
        <span className="av">{row.name.slice(0, 2).toUpperCase()}</span>
        <div>
          <div className="nm">{row.name || short(row.wallet)} <small style={{ color: 'var(--dim)' }}>#{id}</small></div>
          <div className="af">{short(row.wallet)} · {row.jurisdiction}</div>
        </div>
      </div>
      <div className="doms">
        <Pill tone="ctx">{seniorityName}</Pill>
        {row.dsmbVetoRights && <Pill tone="red">DSMB</Pill>}
        {categories.map((c) => (
          <Pill key={c} tone="gray">{c}</Pill>
        ))}
      </div>
      <div className="rep-wrap">
        <div className="rep-l">REPUTATION · {rep} / 300</div>
        <div className="rep-bar"><div className="fill" style={{ width: `${repPct}%` }} /></div>
      </div>
      <div className="stats">
        <div><b>{Number(stats?.assignmentsApproved ?? 0n)}</b>APPROVED</div>
        <div><b>{Number(stats?.assignmentsRejected ?? 0n)}</b>REJECTED</div>
        <div><b>{fmt.meth(weiToEth(row.stake))}</b>METH STAKE</div>
        <div><b>{Number(stats?.assignmentsClosed ?? 0n)}</b>CLOSED</div>
      </div>
    </div>
  );
}

function RegisterCard({
  account, myFirmId, myFirmRow,
}: {
  account: string | null;
  myFirmId: bigint | undefined;
  myFirmRow: FirmRow | undefined;
}) {
  const isRegistered = myFirmId !== undefined && myFirmId !== NOT_FOUND;
  const statusName = isRegistered && myFirmRow ? FIRM_STATUSES[Number(myFirmRow.status)] : null;
  const canOpenForm = !!account && !isRegistered;

  const [open, setOpen] = useState(false);

  return (
    <div className="panel mb-14" style={{ borderColor: 'var(--ctx)' }}>
      <div className="p-head"><h3>{
        !account ? 'Register a firm' :
        !isRegistered ? 'Register a firm' :
        statusName === 'Active' ? 'Your firm is active' :
        `Your firm is ${statusName?.toLowerCase()}`
      }</h3><span className="src"><code>registry.registerFirm(...)</code></span></div>
      <div className="p-body">
        {!account && (
          <p className="help" style={{ marginTop: 0 }}>
            Connect your wallet to register your auditing firm and start
            reviewing milestones.
          </p>
        )}
        {account && !isRegistered && !open && (
          <p className="help" style={{ marginTop: 0 }}>
            Stake METH against a seniority tier (Junior 10 / Senior 100 /
            Principal 1000) and declare audit categories. Stake is
            slashable per the tiered Minor / Major / Critical schedule.
          </p>
        )}
        {account && isRegistered && myFirmRow && (
          <>
            <div className="estim"><span>FIRM ID</span><b>#{String(myFirmId)}</b></div>
            <div className="estim"><span>NAME</span><b>{myFirmRow.name}</b></div>
            <div className="estim"><span>SENIORITY</span><b>{SENIORITY_LEVELS[Number(myFirmRow.seniority)]}</b></div>
            <div className="estim"><span>STAKE</span><b>{fmt.meth(weiToEth(myFirmRow.stake))} METH</b></div>
            <div className="estim"><span>STATUS</span><b style={{ color: statusName === 'Active' ? 'var(--green)' : statusName === 'Banned' ? 'var(--red)' : 'var(--amber)' }}>{statusName}</b></div>
            <div className="estim"><span>DSMB VETO</span><b>{myFirmRow.dsmbVetoRights ? 'YES' : '—'}</b></div>
          </>
        )}

        {canOpenForm && !open && (
          <button
            className="btn btn-prim btn-block btn-lg mt-14"
            onClick={() => setOpen(true)}
          >
            Register firm…
          </button>
        )}

        {canOpenForm && open && (
          <RegisterForm account={account!} onCancel={() => setOpen(false)} />
        )}

        {!canOpenForm && !isRegistered && !account && (
          <button className="btn btn-prim btn-block btn-lg btn-locked mt-14" disabled>
            Connect wallet to register
          </button>
        )}

        {isRegistered && (
          <p className="help mt-14" style={{ marginBottom: 0 }}>
            Stake, categories, and seniority can be managed in the
            <b style={{ color: 'var(--ink)' }}> Firm management </b>
            panel below.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Inline form for registry.registerFirm. Two-step flow:
 *   1. METH approve to the registry for `stake`
 *   2. registerFirm(name, jurisdiction, seniority, categories[], stake)
 *
 * The two writes happen sequentially with toast feedback for each.
 * Allowance is not cached — every register goes through approve. For UX
 * polish in a future commit, read allowance() first and skip approve if
 * already sufficient.
 */
function RegisterForm({ account, onCancel }: { account: string; onCancel: () => void }) {
  const balance = useBalanceOf(account);
  const minJ = useMinStakeJunior();
  const minS = useMinStakeSenior();
  const minP = useMinStakePrincipal();
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const [name, setName] = useState('');
  const [jurisdiction, setJurisdiction] = useState('ES');
  const [seniority, setSeniority] = useState<0 | 1 | 2>(0); // Junior
  const [categories, setCategories] = useState<Set<number>>(new Set([0])); // Generic
  const [stakeInput, setStakeInput] = useState('10');

  const tierMin =
    seniority === 0 ? (minJ.data ?? 10n * 10n ** 18n) :
    seniority === 1 ? (minS.data ?? 100n * 10n ** 18n) :
                      (minP.data ?? 1000n * 10n ** 18n);
  const tierMinMeth = weiToEth(tierMin);

  let stakeWei: bigint | null = null;
  try {
    stakeWei = ethers.parseUnits(stakeInput || '0', 18);
  } catch {
    stakeWei = null;
  }

  const balanceMeth = balance.data !== undefined ? weiToEth(balance.data) : 0;
  const sufficientBal = balance.data !== undefined && stakeWei !== null && balance.data >= stakeWei;
  const sufficientStake = stakeWei !== null && stakeWei >= tierMin;
  const hasName = name.trim().length > 0;
  const hasCats = categories.size > 0;
  const canSubmit = !submitting && hasName && hasCats && sufficientStake && sufficientBal;

  function toggleCategory(idx: number) {
    setCategories((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  async function submit() {
    if (!canSubmit || stakeWei === null) return;
    // Step 1: approve registry to pull `stake` METH from the user.
    const approveOk = await write({
      contract: 'token',
      fn: 'approve',
      args: [ADDRESSES.AuditorRegistryV2, stakeWei],
      options: {
        pendingTitle: 'Approving METH stake…',
        successTitle: `Approved ${fmt.meth(weiToEth(stakeWei))} METH`,
        invalidate: [['metha', 'token']],
      },
    });
    if (!approveOk) return;

    // Step 2: registerFirm.
    const categoriesArr = Array.from(categories).sort((a, b) => a - b);
    const registerOk = await write({
      contract: 'registry',
      fn: 'registerFirm',
      args: [name.trim(), jurisdiction.trim() || 'ES', seniority, categoriesArr, stakeWei],
      options: {
        pendingTitle: 'Registering firm…',
        successTitle: `Firm "${name.trim()}" registered`,
        invalidate: [['metha', 'registry']],
      },
    });
    if (registerOk) {
      onCancel(); // close the form on success
    }
  }

  return (
    <div className="mt-14">
      <div className="form-row">
        <span className="field-l">Firm name</span>
        <div className="input">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Audit Lab" />
        </div>
      </div>
      <div className="form-row">
        <span className="field-l">Jurisdiction (ISO-3166)</span>
        <div className="input">
          <input value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value.toUpperCase())} maxLength={2} placeholder="ES" />
        </div>
      </div>
      <div className="form-row">
        <span className="field-l">Seniority</span>
        <div className="input">
          <select value={seniority} onChange={(e) => setSeniority(Number(e.target.value) as 0 | 1 | 2)}>
            {SENIORITY_LEVELS.map((label, idx) => (
              <option key={idx} value={idx}>{label} (min {weiToEth(idx === 0 ? minJ.data ?? 10n*10n**18n : idx === 1 ? minS.data ?? 100n*10n**18n : minP.data ?? 1000n*10n**18n)} METH)</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row" style={{ alignItems: 'flex-start' }}>
        <span className="field-l">Audit categories</span>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {AUDIT_CATEGORIES.map((label, idx) => (
            <label key={idx} className="mono" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={categories.has(idx)}
                onChange={() => toggleCategory(idx)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="form-row">
        <span className="field-l">Stake (METH)</span>
        <div className="input">
          <input
            value={stakeInput}
            onChange={(e) => setStakeInput(e.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal"
            placeholder={String(tierMinMeth)}
          />
        </div>
      </div>

      <div className="estim"><span>YOUR METH BALANCE</span><b style={{ color: sufficientBal ? 'var(--ink)' : 'var(--red)' }}>{fmt.meth(balanceMeth)} METH</b></div>
      <div className="estim"><span>TIER MINIMUM</span><b>{tierMinMeth} METH</b></div>
      <div className="estim"><span>STAKE TO LOCK</span><b>{stakeWei !== null ? fmt.meth(weiToEth(stakeWei)) : '—'} METH</b></div>

      {!sufficientStake && stakeWei !== null && (
        <p className="help" style={{ color: 'var(--red)', marginTop: 8 }}>
          Stake is below the {SENIORITY_LEVELS[seniority]} tier minimum.
        </p>
      )}
      {!sufficientBal && stakeWei !== null && (
        <p className="help" style={{ color: 'var(--red)', marginTop: 8 }}>
          Insufficient METH balance. Buy more in the auction first.
        </p>
      )}
      {!hasName && (
        <p className="help" style={{ color: 'var(--amber)', marginTop: 8 }}>Name required.</p>
      )}
      {!hasCats && (
        <p className="help" style={{ color: 'var(--amber)', marginTop: 8 }}>Pick at least one category.</p>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          className={`btn btn-prim${canSubmit ? '' : ' btn-locked'}`}
          disabled={!canSubmit}
          onClick={submit}
          style={{ flex: 1 }}
        >
          {submitting ? 'Submitting…' : `Approve & register firm`}
        </button>
      </div>
    </div>
  );
}

/* -------------------- Firm management (post-registration) -------------------- */

/**
 * Post-registration management panel. Three subsections, in priority order:
 *   1. Stake — increaseStake / requestUnstake / finalizeUnstake. Cooldown
 *      countdown is tracked in localStorage because the v3 contract's
 *      `unstakeRequestedAt` is private (getFirm doesn't expose it). When the
 *      contract gains a public getter for it, swap localStorage for a read
 *      hook and delete the cleanup-on-finalize side-effect.
 *   2. Categories — addCategory / removeCategory. Toggling ClinicalSafety off
 *      also auto-revokes DSMB veto on the contract side (see L279 in
 *      AuditorRegistryV2.sol).
 *   3. Seniority — upgradeSeniority(newLevel, additionalStake). Strict
 *      upgrade only (no downgrade path on the contract).
 */
function FirmManagementCard({
  firmId, row, heldCategoryIdx,
}: {
  firmId: number;
  row: FirmRow;
  heldCategoryIdx: Set<number>;
}) {
  const minJ = useMinStakeJunior();
  const minS = useMinStakeSenior();
  const minP = useMinStakePrincipal();
  const cooldown = useUnstakeCooldown();

  const seniorityIdx = Number(row.seniority);
  const currentTierMin =
    seniorityIdx === 0 ? (minJ.data ?? 10n  * 10n ** 18n) :
    seniorityIdx === 1 ? (minS.data ?? 100n * 10n ** 18n) :
                         (minP.data ?? 1000n * 10n ** 18n);
  const cooldownSec = Number(cooldown.data ?? 7n * 86400n);

  const isActive = Number(row.status) === 1;
  const isFrozen = Number(row.status) === 2;
  const isBanned = Number(row.status) === 3;

  const belowMinStake = row.stake < currentTierMin;

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Firm management</h3>
        <span className="src"><code>registry.increase/unstake/category/upgrade</code></span>
      </div>

      {/* 1. STAKE */}
      <StakeSubsection
        firmId={firmId}
        firmStakeWei={row.stake}
        currentTierMinWei={currentTierMin}
        cooldownSec={cooldownSec}
        isActive={isActive}
        isFrozen={isFrozen}
        belowMin={belowMinStake}
      />

      {/* 2. CATEGORIES */}
      {!isBanned && (
        <CategoriesSubsection
          heldCategoryIdx={heldCategoryIdx}
          isActive={isActive}
        />
      )}

      {/* 3. SENIORITY UPGRADE — only meaningful if not already Principal and active */}
      {isActive && seniorityIdx < 2 && (
        <SenioritySubsection
          currentSeniorityIdx={seniorityIdx}
          firmStakeWei={row.stake}
          minS={minS.data}
          minP={minP.data}
        />
      )}
    </div>
  );
}

/* localStorage key for the per-firm unstake cooldown timestamp. v3 contract
 * doesn't expose `unstakeRequestedAt` via getFirm, so this is a soft signal
 * that drives the UI countdown. The on-chain check still enforces correctness. */
function unstakeStorageKey(firmId: number): string {
  return `metha:auditors:unstakeRequestedAt:${firmId}`;
}

function StakeSubsection(p: {
  firmId: number;
  firmStakeWei: bigint;
  currentTierMinWei: bigint;
  cooldownSec: number;
  isActive: boolean;
  isFrozen: boolean;
  belowMin: boolean;
}) {
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const [increaseInput, setIncreaseInput] = useState('');
  let increaseWei: bigint | null = null;
  try {
    increaseWei = increaseInput.trim() ? ethers.parseUnits(increaseInput, 18) : null;
  } catch {
    increaseWei = null;
  }
  const canIncrease = !submitting && p.isActive && increaseWei !== null && increaseWei > 0n;

  // Read the local cooldown timestamp on mount + re-render every second while
  // active so the countdown ticks. Cleared post-finalize.
  const [unstakeStartedAt, setUnstakeStartedAt] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(unstakeStorageKey(p.firmId));
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (unstakeStartedAt === null) return;
    const t = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(t);
  }, [unstakeStartedAt]);

  const cooldownEndsAt = unstakeStartedAt !== null ? unstakeStartedAt + p.cooldownSec : null;
  const cooldownRemaining = cooldownEndsAt !== null ? Math.max(0, cooldownEndsAt - nowSec) : null;
  const cooldownActive = cooldownRemaining !== null && cooldownRemaining > 0;
  const finalizeReady = cooldownRemaining !== null && cooldownRemaining === 0;

  async function doIncrease() {
    if (!canIncrease || increaseWei === null) return;
    // Step 1: approve the registry to pull `amount` METH from the user.
    const okA = await write({
      contract: 'token',
      fn: 'approve',
      args: [ADDRESSES.AuditorRegistryV2, increaseWei],
      options: {
        pendingTitle: 'Approving METH stake…',
        successTitle: `Approved ${fmt.meth(weiToEth(increaseWei))} METH`,
        invalidate: [['metha', 'token']],
      },
    });
    if (!okA) return;
    // Step 2: increaseStake.
    const okB = await write({
      contract: 'registry',
      fn: 'increaseStake',
      args: [increaseWei],
      options: {
        pendingTitle: 'Increasing stake…',
        successTitle: `Stake increased by ${fmt.meth(weiToEth(increaseWei))} METH`,
        invalidate: [['metha', 'registry']],
      },
    });
    if (okB) setIncreaseInput('');
  }

  async function doRequestUnstake() {
    if (submitting || !p.isActive) return;
    const ok = await write({
      contract: 'registry',
      fn: 'requestUnstake',
      args: [],
      options: {
        pendingTitle: 'Requesting unstake…',
        successTitle: `Unstake requested — cooldown ${Math.round(p.cooldownSec / 86400)} days`,
        invalidate: [['metha', 'registry']],
      },
    });
    if (ok && typeof window !== 'undefined') {
      // The actual block.timestamp may differ by a few seconds from the
      // wall-clock; that's fine since the contract is authoritative on
      // whether finalize is callable. We round up to be slightly cautious.
      const startedAt = Math.floor(Date.now() / 1000);
      window.localStorage.setItem(unstakeStorageKey(p.firmId), String(startedAt));
      setUnstakeStartedAt(startedAt);
    }
  }

  async function doFinalizeUnstake() {
    if (submitting || !finalizeReady) return;
    const ok = await write({
      contract: 'registry',
      fn: 'finalizeUnstake',
      args: [],
      options: {
        pendingTitle: 'Finalizing unstake…',
        successTitle: 'Unstake finalized — stake returned, firm inactivated',
        invalidate: [['metha', 'registry'], ['metha', 'token']],
      },
    });
    if (ok && typeof window !== 'undefined') {
      window.localStorage.removeItem(unstakeStorageKey(p.firmId));
      setUnstakeStartedAt(null);
    }
  }

  return (
    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
      <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
        Stake
      </div>

      <div className="estim"><span>CURRENT STAKE</span><b style={{ color: p.belowMin ? 'var(--red)' : 'var(--ink)' }}>{fmt.meth(weiToEth(p.firmStakeWei))} METH</b></div>
      <div className="estim"><span>TIER MINIMUM</span><b>{fmt.meth(weiToEth(p.currentTierMinWei))} METH</b></div>
      {p.belowMin && (
        <p className="help" style={{ color: 'var(--red)', marginTop: 4, marginBottom: 10 }}>
          Below tier minimum — likely a post-slashing state. Top up to return to {p.isFrozen ? 'Active' : 'compliance'}.
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
        <input
          className="mono"
          style={{ flex: 1, padding: '6px 10px', fontSize: 11, background: 'var(--panel-2)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
          placeholder="amount to add (METH)"
          value={increaseInput}
          onChange={(e) => setIncreaseInput(e.target.value.replace(/[^0-9.]/g, ''))}
          inputMode="decimal"
        />
        <button className={`btn ${canIncrease ? 'btn-prim' : 'btn-locked'}`} disabled={!canIncrease} onClick={doIncrease}>
          Increase stake
        </button>
      </div>

      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--line)' }}>
        <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 8 }}>
          Exit (7-day cooldown)
        </div>
        {!cooldownActive && !finalizeReady && (
          <button
            className={`btn btn-block ${p.isActive && !submitting ? '' : 'btn-locked'}`}
            disabled={!p.isActive || submitting}
            onClick={doRequestUnstake}
            title={!p.isActive ? 'Firm not Active' : undefined}
          >
            Request unstake (starts cooldown)
          </button>
        )}
        {cooldownActive && cooldownRemaining !== null && (
          <div className="estim" style={{ background: 'var(--panel-2)', padding: '8px 10px', borderRadius: 4 }}>
            <span>COOLDOWN</span>
            <b style={{ color: 'var(--amber)' }}>{fmtCountdown(cooldownRemaining)}</b>
          </div>
        )}
        {finalizeReady && (
          <button
            className={`btn btn-prim btn-block${submitting ? ' btn-locked' : ''}`}
            disabled={submitting}
            onClick={doFinalizeUnstake}
            style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
          >
            Finalize unstake — return {fmt.meth(weiToEth(p.firmStakeWei))} METH
          </button>
        )}
        {(cooldownActive || finalizeReady) && (
          <p className="help mt-14" style={{ marginBottom: 0, color: 'var(--dim)', fontSize: 10 }}>
            Cooldown tracked via local browser state. On-chain check is authoritative.
          </p>
        )}
      </div>
    </div>
  );
}

function CategoriesSubsection(p: {
  heldCategoryIdx: Set<number>;
  isActive: boolean;
}) {
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  function toggle(catIdx: number, currentlyHeld: boolean) {
    if (submitting || !p.isActive) return;
    const fn = currentlyHeld ? 'removeCategory' : 'addCategory';
    return write({
      contract: 'registry',
      fn,
      args: [catIdx],
      options: {
        pendingTitle: currentlyHeld ? `Removing ${AUDIT_CATEGORIES[catIdx]}…` : `Adding ${AUDIT_CATEGORIES[catIdx]}…`,
        successTitle: currentlyHeld ? `Removed ${AUDIT_CATEGORIES[catIdx]}` : `Added ${AUDIT_CATEGORIES[catIdx]}`,
        invalidate: [['metha', 'registry']],
      },
    });
  }

  return (
    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
      <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
        Audit categories
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {AUDIT_CATEGORIES.map((label, idx) => {
          const held = p.heldCategoryIdx.has(idx);
          const enabled = p.isActive && !submitting;
          return (
            <button
              key={label}
              className={`btn btn-sm ${held ? 'btn-prim' : ''}${!enabled ? ' btn-locked' : ''}`}
              disabled={!enabled}
              onClick={() => toggle(idx, held)}
              title={held
                ? (label === 'ClinicalSafety'
                  ? 'Click to remove — also auto-revokes DSMB veto'
                  : 'Click to remove')
                : 'Click to add'}
              style={!enabled ? undefined : held ? { background: 'var(--ctx)', borderColor: 'var(--ctx)' } : undefined}
            >
              {held ? '−' : '+'} {label}
            </button>
          );
        })}
      </div>
      <p className="help mt-14" style={{ marginBottom: 0, color: 'var(--dim)', fontSize: 10 }}>
        Removing <span className="mono">ClinicalSafety</span> also revokes DSMB veto rights on the contract.
      </p>
    </div>
  );
}

function SenioritySubsection(p: {
  currentSeniorityIdx: number;
  firmStakeWei: bigint;
  minS: bigint | undefined;
  minP: bigint | undefined;
}) {
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  // Strict-upgrade only: can pick any tier strictly greater than current.
  const upgradeOptions = SENIORITY_LEVELS
    .map((label, idx) => ({ label, idx }))
    .filter((o) => o.idx > p.currentSeniorityIdx);
  const [targetIdx, setTargetIdx] = useState<number>(upgradeOptions[0]?.idx ?? p.currentSeniorityIdx + 1);
  const [extraInput, setExtraInput] = useState('');

  let extraWei: bigint | null = 0n;
  if (extraInput.trim()) {
    try { extraWei = ethers.parseUnits(extraInput, 18); } catch { extraWei = null; }
  }
  const targetMin =
    targetIdx === 1 ? (p.minS ?? 100n  * 10n ** 18n) :
    targetIdx === 2 ? (p.minP ?? 1000n * 10n ** 18n) :
                      0n;
  const projectedStake = extraWei !== null ? p.firmStakeWei + extraWei : p.firmStakeWei;
  const meetsTarget = projectedStake >= targetMin;
  const canUpgrade = !submitting && extraWei !== null && meetsTarget && targetIdx > p.currentSeniorityIdx;

  async function doUpgrade() {
    if (!canUpgrade || extraWei === null) return;
    // Only approve when we'll actually transfer additional stake.
    if (extraWei > 0n) {
      const okA = await write({
        contract: 'token',
        fn: 'approve',
        args: [ADDRESSES.AuditorRegistryV2, extraWei],
        options: {
          pendingTitle: 'Approving additional stake…',
          successTitle: `Approved ${fmt.meth(weiToEth(extraWei))} METH`,
          invalidate: [['metha', 'token']],
        },
      });
      if (!okA) return;
    }
    const okB = await write({
      contract: 'registry',
      fn: 'upgradeSeniority',
      args: [targetIdx, extraWei],
      options: {
        pendingTitle: `Upgrading to ${SENIORITY_LEVELS[targetIdx]}…`,
        successTitle: `Seniority upgraded to ${SENIORITY_LEVELS[targetIdx]}`,
        invalidate: [['metha', 'registry'], ['metha', 'token']],
      },
    });
    if (okB) setExtraInput('');
  }

  return (
    <div style={{ padding: '14px 18px' }}>
      <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
        Seniority upgrade
      </div>
      <div className="estim"><span>CURRENT</span><b>{SENIORITY_LEVELS[p.currentSeniorityIdx]}</b></div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <div className="input" style={{ flex: 1 }}>
          <select
            value={targetIdx}
            onChange={(e) => setTargetIdx(Number(e.target.value))}
            style={{ width: '100%', background: 'transparent', color: 'var(--ink)', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 'inherit' }}
          >
            {upgradeOptions.map((o) => (
              <option key={o.idx} value={o.idx} style={{ background: 'var(--panel)', color: 'var(--ink)' }}>
                {o.label} (min {fmt.meth(weiToEth(o.idx === 1 ? (p.minS ?? 100n * 10n ** 18n) : (p.minP ?? 1000n * 10n ** 18n)))} METH)
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input
          className="mono"
          style={{ flex: 1, padding: '6px 10px', fontSize: 11, background: 'var(--panel-2)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
          placeholder="additional stake (METH) — optional"
          value={extraInput}
          onChange={(e) => setExtraInput(e.target.value.replace(/[^0-9.]/g, ''))}
          inputMode="decimal"
        />
      </div>
      <div className="estim mt-14"><span>PROJECTED STAKE</span><b style={{ color: meetsTarget ? 'var(--ink)' : 'var(--red)' }}>{fmt.meth(weiToEth(projectedStake))} METH</b></div>
      <div className="estim"><span>TARGET MIN</span><b>{fmt.meth(weiToEth(targetMin))} METH</b></div>
      {!meetsTarget && extraWei !== null && (
        <p className="help" style={{ color: 'var(--red)', marginTop: 6, marginBottom: 0 }}>
          Projected stake is below the {SENIORITY_LEVELS[targetIdx]} minimum.
        </p>
      )}
      <button
        className={`btn btn-prim btn-block mt-14${canUpgrade ? '' : ' btn-locked'}`}
        disabled={!canUpgrade}
        onClick={doUpgrade}
      >
        {submitting ? 'Submitting…' : `Upgrade to ${SENIORITY_LEVELS[targetIdx]}`}
      </button>
    </div>
  );
}

/** "6d 23h 14m" / "23h 14m" / "14m 21s" / "21s" — depending on magnitude. */
function fmtCountdown(remainingSec: number): string {
  if (remainingSec <= 0) return 'ready';
  const d = Math.floor(remainingSec / 86400);
  const h = Math.floor((remainingSec % 86400) / 3600);
  const m = Math.floor((remainingSec % 3600) / 60);
  const s = remainingSec % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function Kpi({ l, v, d, tone }: { l: string; v: ReactNode; d: ReactNode; tone?: 'up' | 'dn' }) {
  return (
    <div className="kpi">
      <span className="l">{l}</span>
      <span className="v">{v}</span>
      <span className={`d${tone ? ' ' + tone : ''}`}>{d}</span>
    </div>
  );
}
